import { useEffect, useState } from 'react';

import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { IconLeftTailedArrow, IconRightTailedArrow } from '@/components/Icons';
import { type InterviewBasicDto } from '@/services/api';

import { useGetCompanyUserCreatedInterviews } from './queries';

interface UserInterviewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName?: string;
}

export const UserInterviewsModal: React.FC<UserInterviewsModalProps> = ({
  isOpen,
  onClose,
  userId,
}) => {
  const { t } = useTranslation(['common', 'components', 'interview']);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
    }
  }, [isOpen, userId]);

  const { data: interviewsData, isLoading } =
    useGetCompanyUserCreatedInterviews(userId, {
      page: currentPage,
      size: pageSize,
    });

  const interviews = interviewsData?.data ?? [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="16px" maxW="624px" maxH="740px">
        <ModalCloseButton
          position="absolute"
          right="12px"
          top="12px"
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.200"
          bg="white"
          w="36px"
          h="36px"
          zIndex={10}
        />

        <ModalHeader textAlign="center" pb={4}>
          <Text
            fontSize="20px"
            fontWeight="600"
            color="#2F3236"
            lineHeight="1.4"
            letterSpacing="-0.02em"
          >
            {t('components:layout.interviews')}
          </Text>
        </ModalHeader>

        <ModalBody px={6} pb={6}>
          <Box
            borderWidth="1px"
            borderColor="#E9EAED"
            borderRadius="12px"
            overflow="hidden"
          >
            <Box
              bg="#FAFBFB"
              borderBottomWidth="1px"
              borderBottomColor="#E9EAED"
            >
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th
                      py={4}
                      px={6}
                      borderRightWidth="1px"
                      borderRightColor="#E9EAED"
                      bg="transparent"
                      fontSize="16px"
                      fontWeight="400"
                      color="#6F7881"
                      letterSpacing="-0.02em"
                    >
                      {t('interview:videoInterview.interviewName')}
                    </Th>
                    <Th
                      py={4}
                      px={6}
                      borderRightWidth="1px"
                      borderRightColor="#E9EAED"
                      bg="transparent"
                      fontSize="16px"
                      fontWeight="400"
                      color="#6F7881"
                      letterSpacing="-0.02em"
                    >
                      {t('components:board.creationDate')}
                    </Th>
                  </Tr>
                </Thead>
              </Table>
            </Box>

            <Box maxH="400px" overflowY="auto">
              {isLoading ? (
                <VStack py={8} spacing={4}>
                  <Spinner size="md" />
                </VStack>
              ) : interviews.length === 0 ? (
                <VStack py={8} spacing={4}>
                  <Text color="gray.500">{t('interview:noInterview')}</Text>
                </VStack>
              ) : (
                <Table variant="simple" size="sm">
                  <Tbody>
                    {interviews.map((interview: InterviewBasicDto) => (
                      <Tr
                        key={interview.id}
                        borderBottomWidth="1px"
                        borderBottomColor="#E9EAED"
                      >
                        <Td
                          py={3}
                          px={6}
                          borderRightWidth="1px"
                          borderRightColor="#E9EAED"
                          bg="transparent"
                          fontSize="16px"
                          fontWeight="500"
                          color="#2F3236"
                          letterSpacing="-0.02em"
                        >
                          {interview.title}
                        </Td>
                        <Td
                          py={3}
                          px={6}
                          borderRightWidth="1px"
                          borderRightColor="#E9EAED"
                          bg="transparent"
                          fontSize="16px"
                          fontWeight="500"
                          color="#6F7881"
                          letterSpacing="-0.02em"
                        >
                          {interview.createdAt
                            ? dayjs(interview.createdAt).format(
                                'DD.MM.YYYY - HH:mm'
                              )
                            : '-'}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </Box>
          </Box>

          {!!interviewsData &&
            !!interviewsData.total &&
            interviewsData.total > 0 && (
              <Box
                px={4}
                py={3}
                borderTopWidth="1px"
                borderTopColor="#E9EAED"
                bg="white"
                display="flex"
                justifyContent="flex-end"
                w="full"
                mt={4}
              >
                <HStack
                  justify="space-between"
                  align="center"
                  bg="white"
                  w="fit-content"
                  border="1px solid #DEDFE2"
                  borderRadius="12px"
                  px={3}
                  py={1.5}
                  spacing={1}
                >
                  <Text
                    fontSize="16px"
                    color="#6F7881"
                    fontWeight="400"
                    pr={2}
                    borderRight="1px solid #DEDFE2"
                  >
                    {(currentPage - 1) * pageSize + 1}-
                    {Math.min(currentPage * pageSize, interviewsData.total)} of{' '}
                    {interviewsData.total}
                  </Text>

                  <HStack spacing={1}>
                    <Button
                      size="sm"
                      variant="unstyled"
                      onClick={() =>
                        setCurrentPage(Math.max(1, currentPage - 1))
                      }
                      isDisabled={currentPage === 1}
                      minW="32px"
                      h="32px"
                      p={1}
                      bg="white"
                      borderRadius="0"
                      _hover={{ bg: 'gray.50' }}
                      _disabled={{
                        opacity: 0.5,
                        cursor: 'not-allowed',
                      }}
                      rounded="md"
                      boxShadow="none"
                    >
                      <IconLeftTailedArrow
                        color={currentPage === 1 ? '#BDC1C5' : '#656D75'}
                      />
                    </Button>

                    <Button
                      size="sm"
                      variant="unstyled"
                      onClick={() =>
                        setCurrentPage(
                          Math.min(
                            interviewsData.totalPages ?? 1,
                            currentPage + 1
                          )
                        )
                      }
                      isDisabled={
                        currentPage >= (interviewsData.totalPages ?? 1)
                      }
                      minW="32px"
                      h="32px"
                      p={1}
                      bg="white"
                      borderRadius="0"
                      _hover={{ bg: 'gray.50' }}
                      _disabled={{
                        opacity: 0.5,
                        cursor: 'not-allowed',
                      }}
                      rounded="md"
                      boxShadow="none"
                    >
                      <IconRightTailedArrow
                        color={
                          currentPage >= (interviewsData.totalPages ?? 1)
                            ? '#BDC1C5'
                            : '#656D75'
                        }
                      />
                    </Button>
                  </HStack>
                </HStack>
              </Box>
            )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
